import React, { useState } from "react";

import { faFile, faGripDotsVertical, faPlus, faUser } from "@fortawesome/pro-regular-svg-icons";
import { faCircleXmark, faFilePdf as faFilePdfSolid } from "@fortawesome/pro-solid-svg-icons";

import {
	ButtonIcon,
	ButtonStandard,
	FormField,
	FormInputNumeric,
	FormInputText,
	FormList,
	Icon,
	InputText,
	InputWrapper,
	SortableDragNDrop,
	Text,
} from "@performyard/py-components";
import type { DragHandlers } from "@performyard/py-components";

import type { AssigneeValue } from "./AssigneeSelect";
import AssigneeSelect from "./AssigneeSelect";
import SignatureTemplateDetailsModal from "./SignatureTemplateDetailsModal";
import SignatureTemplatePickerModal from "./SignatureTemplatePickerModal";

import { TODOS_MAX_COUNT, TodoRow, buildEmptyTodoRow } from "./todoForm";

type DocModalState = {
	rowId: string;
	mode: "pick" | "view";
};

const describeSignerCount = (row: TodoRow): string => {
	const count = row.signatureTemplate?.signerCount;
	if (typeof count !== "number") return row.signatureTemplate?.name || "Signature Template";
	return `${count} ${count === 1 ? "Signer" : "Signers"}`;
};

// Shared by the header row and every to-do row so the two stay in the same columns.
// The row stays on one line at every width (per the design): the name column carries the
// flex basis so all three shrink together instead of the name collapsing to nothing
// first, and the assignee/days columns keep their 240/110 design widths until the card
// runs out of room, then shrink to a floor rather than overflowing it.
const ICON_CELL = "flex h-8 w-6 shrink-0 items-center justify-center";
const NAME_CELL = "min-w-0 grow basis-[180px]";
const ASSIGNEE_CELL = "w-[240px] min-w-[140px] shrink";
const DAYS_CELL = "w-[110px] min-w-[88px] shrink";

const TodoListHeader = ({ docSigningEnabled }: { docSigningEnabled: boolean }) => (
	<div className="flex items-center gap-2">
		<div className="w-6 shrink-0" />
		{docSigningEnabled && <div className="w-6 shrink-0" />}
		<div className={NAME_CELL}>
			<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
				To-Do
			</Text>
		</div>
		<div className={ASSIGNEE_CELL}>
			<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
				Assignee
			</Text>
		</div>
		<div className={DAYS_CELL}>
			<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
				Days to Complete
			</Text>
		</div>
		<div className="w-6 shrink-0" />
	</div>
);

const TodoRowItem = ({
	row,
	index,
	dragHandlers,
	isDragging,
	isOverlay,
	docSigningEnabled,
	onOpenDocModal,
	onRemove,
}: {
	row: TodoRow;
	index: number;
	dragHandlers: DragHandlers;
	isDragging: boolean;
	isOverlay: boolean;
	docSigningEnabled: boolean;
	onOpenDocModal: (mode: DocModalState["mode"]) => void;
	onRemove: () => void;
}) => {
	const hasSignature = !!row.signatureTemplate;

	return (
		<div
			// Whole row is the drag activator, per SortableDragNDrop's "whole container
			// draggable" story: its PointerSensor has a 10px activation distance, so a
			// pointerdown on a field inside the row still reaches the field.
			{...dragHandlers.attributes}
			{...dragHandlers.listeners}
			aria-label="Reorder to-do"
			// Matches the meetings agenda/action items: the dragged row lifts into the drag
			// overlay as an elevated card while the row left behind in the list goes
			// invisible, so the other rows slide into a clean gap.
			//
			// The overlay's `-my-0.5 py-0.5` is a pure visual bleed, not a layout change: the
			// negative margin pulls the card up by the same 2px the padding pushes its
			// contents back down, so the fields stay pinned to the row the drag started on
			// while the card (and its shadow) breathes above and below them. The row left
			// behind keeps its original height, so nothing in the list shifts on grab.
			className={`flex items-start gap-2 ${
				isOverlay
					? "pointer-events-none -my-1 cursor-grabbing rounded bg-neutral-0 py-1 shadow-lg"
					: isDragging
						? "opacity-0"
						: "cursor-grab"
			}`}
			data-testid="todo-row"
		>
			{/* Affordance only -- the activator is the row itself. */}
			<div className={ICON_CELL} data-testid="todo-drag-handle">
				<Icon color="neutral-5" size={24} icon={faGripDotsVertical} aria-hidden="true" />
			</div>

			{/* Gated like the legacy page: hidden when company doc-signing is off, but an
			    already-attached signature still shows in the assignee column below. */}
			{docSigningEnabled && (
				<div className={ICON_CELL}>
					<ButtonIcon
						variant="standard"
						color={hasSignature ? "primary" : "neutral"}
						shade={5}
						size={24}
						icon={hasSignature ? faFilePdfSolid : faFile}
						aria-label={hasSignature ? "View signature request" : "Attach signature document"}
						data-testid={`todo-document-button-${index}`}
						onClick={() => onOpenDocModal(hasSignature ? "view" : "pick")}
					/>
				</div>
			)}

			<div className={NAME_CELL}>
				<FormInputText id={`todos[${index}].name`} placeholder="Enter a to-do name..." aria-label="To-Do" />
			</div>

			<div className={ASSIGNEE_CELL}>
				{hasSignature ? (
					<InputWrapper id={`todos[${index}].signature-assignee`}>
						<InputText
							value={describeSignerCount(row)}
							onChange={() => undefined}
							disabled
							leftIcon={faUser}
							aria-label="Assignee"
							data-testid={`todo-signature-chip-${index}`}
						/>
					</InputWrapper>
				) : (
					<FormField<AssigneeValue | null>
						id={`todos[${index}].assignee`}
						input={(field) => (
							<AssigneeSelect
								value={field.value}
								onChange={(assignee) => field.onChange(assignee)}
								errored={field.errors.length > 0}
								id={`todos[${index}].assignee`}
								data-testid={`todo-assignee-${index}`}
							/>
						)}
					/>
				)}
			</div>

			<div className={DAYS_CELL}>
				<FormInputNumeric
					id={`todos[${index}].daysToComplete`}
					placeholder="e.g. 5"
					min={1}
					precision={0}
					allowEmpty
				/>
			</div>

			<div className={ICON_CELL}>
				<ButtonIcon
					variant="standard"
					color="neutral"
					shade={5}
					size={24}
					icon={faCircleXmark}
					aria-label="Remove to-do"
					data-testid={`todo-remove-${index}`}
					onClick={onRemove}
				/>
			</div>
		</div>
	);
};

/**
 * The reorderable to-do list inside the template editor's PYForm. Rows carry a
 * client-only `_id` so drag-and-drop and React keys survive reordering; all row state
 * lives in the form's `todos` array field.
 */
const TodoListEditor = ({ docSigningEnabled }: { docSigningEnabled: boolean }) => {
	const [docModal, setDocModal] = useState<DocModalState | null>(null);

	return (
		<FormList<TodoRow>
			id="todos"
			input={(field) => {
				const rows = field.value ?? [];
				const docModalIndex = docModal ? rows.findIndex((r) => r._id === docModal.rowId) : -1;
				const docModalRow = docModalIndex >= 0 ? rows[docModalIndex] : null;

				const setSignatureTemplate = (index: number, signatureTemplate: TodoRow["signatureTemplate"]) => {
					const current = rows[index];
					if (!current) return;
					field.replaceValue(index, { ...current, signatureTemplate });
				};

				return (
					<div className="flex flex-col gap-4">
						{rows.length > 0 && (
							<div className="flex flex-col gap-2">
								<TodoListHeader docSigningEnabled={docSigningEnabled} />
								<SortableDragNDrop
									items={rows}
									onDragEnd={(event) => {
										const { active, over } = event;
										if (over && active.id !== over.id) {
											const oldIndex = rows.findIndex((r) => r._id === active.id);
											const newIndex = rows.findIndex((r) => r._id === over.id);
											if (oldIndex !== -1 && newIndex !== -1) {
												field.shiftValue(oldIndex, newIndex);
											}
										}
									}}
									restrictToParentElement
									restrictToVerticalAxis
									showDragOverlay
									useUnorderedList
									listAriaLabel="To-dos"
									data-testid="todo-sortable-list"
									listClassName="m-0 flex list-none flex-col gap-2 p-0"
									renderItem={(item, { dragHandlers, isDragging, isOverlay }) => {
										const index = rows.findIndex((r) => r._id === item._id);
										if (index === -1) return null;
										const row = rows[index] ?? item;
										return (
											<TodoRowItem
												row={row}
												index={index}
												dragHandlers={dragHandlers}
												isDragging={isDragging}
												isOverlay={isOverlay}
												docSigningEnabled={docSigningEnabled}
												onOpenDocModal={(mode) => setDocModal({ rowId: row._id, mode })}
												onRemove={() => field.removeValue(index)}
											/>
										);
									}}
								/>
							</div>
						)}

						{rows.length >= TODOS_MAX_COUNT && (
							<Text size="caption" weight="semi-bold" color="red-6">
								You have reached the maximum number of to-dos
							</Text>
						)}

						<ButtonStandard
							type="button"
							color="primary"
							iconLeft={faPlus}
							disabled={rows.length >= TODOS_MAX_COUNT}
							data-testid="add-todo-button"
							onClick={() => {
								if (rows.length >= TODOS_MAX_COUNT) return;
								field.pushValue(buildEmptyTodoRow());
							}}
						>
							Add To-Do
						</ButtonStandard>

						{docModal?.mode === "pick" && docModalIndex >= 0 && (
							<SignatureTemplatePickerModal
								onClose={() => setDocModal(null)}
								onSelect={(selection) => {
									setSignatureTemplate(docModalIndex, selection);
									setDocModal(null);
								}}
							/>
						)}

						{docModal?.mode === "view" && docModalRow?.signatureTemplate && (
							<SignatureTemplateDetailsModal
								templateId={docModalRow.signatureTemplate.id}
								templateName={docModalRow.signatureTemplate.name}
								onClose={() => setDocModal(null)}
								onRemove={() => {
									setSignatureTemplate(docModalIndex, null);
									setDocModal(null);
								}}
							/>
						)}
					</div>
				);
			}}
		/>
	);
};

export default TodoListEditor;
